import { PkIcon, PkSpinner } from '@uefadigital/panenka-react';
import clsx from 'clsx';
import { memo, useEffect, useMemo, useRef, useState } from 'react';

import PlayerSearch, { SearchType } from '../player-search';
import type { PlayerWithTeam } from '../player-search/types';

import Avatar from './avatar';

import { useTranslateFn } from '@shared/Translate';
import type { Player } from '@shared-types/match.types';

import * as classes from './styles.module.scss';

type H2HPlayerSelectorProps = {
  redirectUrl: string;
  competitionFolder: string;
  players: string[];
};

const H2HPlayerSelector = ({ options }: { options: H2HPlayerSelectorProps }) => {
  const t = useTranslateFn();
  const [player1, setPlayer1] = useState(null);
  const [player2, setPlayer2] = useState(null);
  const [showSelection, setShowSelection] = useState(false);
  const currentPlayer = useRef<'player1' | 'player2'>(null);
  const playerLoaded = useRef(false);
  const fromClick = useRef(false);
  const { redirectUrl } = options;
  const baseUrl = redirectUrl.substring(0, redirectUrl.indexOf('{player1}'));

  const handleClickPlayer = (name: 'player1' | 'player2') => () => {
    setShowSelection(true);
    currentPlayer.current = name;
  };

  const handleClickItem = (player: Player) => {
    setShowSelection(false);

    if (player) {
      if (currentPlayer.current === 'player1') {
        setPlayer1(player);
      } else {
        setPlayer2(player);
      }
    }
    fromClick.current = true;
  };

  const handleCloseSelector = () => {
    setShowSelection(false);
  };

  const handlePlayersLoad = (players: PlayerWithTeam[]) => {
    if (playerLoaded.current) return;
    playerLoaded.current = true;

    const [player1Model, player2Model] = options.players;

    const [newPlayer1, newPlayer2] = players.filter((player) => {
      return player.id === player1Model || player.id === player2Model;
    });

    if (!!player1Model && !!newPlayer1) {
      setPlayer1(newPlayer1.id === player1Model ? newPlayer1 : newPlayer2);
    }

    if (!!player2Model && !!newPlayer2) {
      setPlayer2(newPlayer2.id === player2Model ? newPlayer2 : newPlayer1);
    }

    fromClick.current = false;
  };

  const itShouldRedirect = useMemo(() => player1 && player2 && fromClick.current, [player1, player2, fromClick]);

  useEffect(() => {
    if (itShouldRedirect) {
      if (player1.id === player2.id) {
        setPlayer1(null);
        setPlayer2(null);
        if (window.location.pathname !== baseUrl) {
          window.location.pathname = baseUrl;
        }
      } else {
        window.location.pathname = redirectUrl.replace('{player1}', player1.id).replace('{player2}', player2.id);
      }
    }
  }, [player1, player2, itShouldRedirect, baseUrl, redirectUrl]);

  const newOptions = {
    ...options,
    type: SearchType.FULLSCREEN,
    placeholder: t('search_for_players_or_teams'),
  };

  return (
    <>
      {itShouldRedirect && <PkSpinner className={classes.loader} />}
      <div className={classes.h2HSelectorContainer}>
        <Avatar onClick={handleClickPlayer('player1')} player={player1} competition={newOptions.competitionFolder} />
        <span className={classes.vs}>{t('history_h2h_versus')}</span>
        <Avatar onClick={handleClickPlayer('player2')} player={player2} competition={newOptions.competitionFolder} />
      </div>
      {showSelection && <div className={classes.selectionOverlay} onClick={handleCloseSelector} aria-hidden />}
      <div
        className={clsx({
          [classes.playerSearchContainer]: true,
          [classes.visible]: showSelection,
        })}
      >
        <div className={classes.playerSearchTitleContainer}>
          <div className={classes.playerSearchTitle}>
            <span>{t('history_h2h_select_player')}</span>
            <PkIcon
              size={16}
              name='close-web-icon'
              color='interaction-compl'
              className={classes.playerSearchTitleIcon}
              onClick={handleCloseSelector}
            />
          </div>
        </div>
        <PlayerSearch options={newOptions} onItemClick={handleClickItem} onPlayersLoad={handlePlayersLoad} />
      </div>
    </>
  );
};

export default memo(H2HPlayerSelector);
